import React from 'react';
import {
  QuestionWrapper,
  QuestionLabel,
  QuestionNote,
  QuestionHelpText,
} from './DynamicQuestionForm.styled';
import { QuestionFieldProps } from '../../../types/questionForm';
import FileUploadNotice from '../FileUpload/FileUploadNotice';
import { FileUploadPopUp } from '../FileUpload';
import { useQuestionField } from './hooks/useQuestionField';
import { BasicFieldRenderer } from './renderers/BasicFieldRenderer';
import { RadioFieldRenderer } from './renderers/RadioFieldRenderer';
import ApproveSourceSection from './ApproveSourceSection';
// Import directly to avoid circular dependency
import { useAuth } from '../../../contexts/AuthContext';
import { useQuestionRefs } from '../../../contexts/QuestionRefsContext';
import { FileLock2 } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { HelpCircleIcon } from '../../../assets/icons/HelpCircleIcon';
import { isSuperAdmin } from '../../../constants/roles';
import { useLocation } from 'react-router-dom';
import { useHotelDetail } from '../../../contexts/HotelDetailContext';

export const QuestionField: React.FC<QuestionFieldProps> = ({
  question,
  value,
  onChange,
  answers,
  onAnswerChange,
  rejectionReasons,
  onReviewChange,
  onUploadCompleteInSources,
  hasCountryChangedRef,
  hasDestinationChangedRef,
}) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isInSampleQuestionnaire = pathname.includes('sample-questionnaire');

  // console.log("Question inside questionfield::", question);

  // Try to get the context, but don't throw error if not available (for Sample Questionnaire)
  // let registerRef:
  //   | ((questionId: string, element: HTMLDivElement | null) => void)
  //   | null = null;
  // try {
  //   const context = useQuestionRefs();
  //   registerRef = context.registerRef;
  // } catch (error) {
  //   registerRef = null;
  // }

  const { registerRef } = useQuestionRefs();
  const { hotel } = useHotelDetail();
  const {
    currentStepKey,
    handleFileChange,
    shouldShowUploadSection,
    isUploadDisabled,
  } = useQuestionField(question, value);

  const renderUploadSection = () => {
    if (currentStepKey === 'sources') {
      return (
        <FileUploadPopUp
          questionId={question.id}
          question={question}
          maxFiles={20}
          simplified={true}
          attachments={question.attachments || []}
          onUploadCompleteInSources={onUploadCompleteInSources}
          disabled={question.attributes?.readOnly || isUploadDisabled()} //disable the upload if the question is readonly or if the useUploadDisabled from the hook returns true
        />
      );
    }
    return (
      <FileUploadNotice
        questionId={question.id}
        isReadOnly={isInSampleQuestionnaire && question.attributes?.readOnly}
        uploadedFilenames={question.uploadedFilenames}
        showImportantNotice={currentStepKey === 'certifications'}
      />
    );
  };

  const renderApproveSection = () => {
    
    // Only show approval section if:
    // 1. User is ALO_SUPER
    // 2. Current step is sources
    // 3. Question has attachments
    const canApproveSource = hotel?.userPermissions?.canApproveSource;
    const hasAttachments = question.attachments && question.attachments.length > 0;
    const showApproveSection =
      canApproveSource &&
      isSuperAdmin(user?.role) &&
      currentStepKey === 'sources' && hasAttachments;
    if (showApproveSection) {
      return (
        <ApproveSourceSection
          question={question}
          rejectionReasons={rejectionReasons}
          onReviewChange={onReviewChange}
        />
      );
    }
    return null;
  };

  const renderField = () => {
    // Handle all radio-based fields with dedicated renderer
    if (question.type === 'radio-text' || question.type === 'radio/text') {
      return (
        <RadioFieldRenderer
          question={question}
          value={value}
          renderUploadSection={renderUploadSection}
          renderApproveSection={renderApproveSection}
          shouldShowUploadSection={shouldShowUploadSection}
          currentStepKey={currentStepKey}
        />
      );
    }

    // Handle all other field types with basic renderer
    return (
      <BasicFieldRenderer
        question={question}
        value={value}
        handleFileChange={handleFileChange(onChange)}
        renderUploadSection={renderUploadSection}
        renderApproveSection={renderApproveSection}
        shouldShowUploadSection={shouldShowUploadSection}
        answers={answers}
        currentStepKey={currentStepKey}
        isInSampleQuestionnaire={isInSampleQuestionnaire}
        {...(hasCountryChangedRef && { hasCountryChangedRef })}
        {...(hasDestinationChangedRef && { hasDestinationChangedRef })}
      />
    );
  };

  const renderCertificateOverrideNotice = () => {
    //do not show the icon for the question which has subquestions since, the main question id and the first subquestion id will be same
    if (question.hasSubQuestions) return;
    if (answers && answers[question.id]?.isCertificateOverride) {
      return (
        <Tooltip
          content="This question has been answered as part of a provided certification"
          position="top"
          icon={
            <span className="bg-planet-green px-3 p-1 rounded-xl inline-flex items-center justify-center">
              <FileLock2 className="inline text-white" size={16} />
            </span>
          }
          maxWidth={280}
        />
      );
    }
    return null;
  };

  const renderTooltip = () => {
    if (question.tooltip_text) {
      return (
          <Tooltip
            content={question.tooltip_text}
            position="top"
            className=''
            icon={
              <HelpCircleIcon
                color="var(--color-gray-400)"
                className="w-4 h-4 inline ml-2"
              />
            }
          />
      );
    }
    return null;
  };

  return (
    <QuestionWrapper
      $colSpan={question.colSpan}
      ref={el => {
        // Set ref for questions without subquestions OR for custom types (like radio-grid)
        if (
          !question.subQuestions ||
          question.subQuestions.length === 0 ||
          question.type === 'custom'
        ) {
          if (
            question.type === 'custom' &&
            (question as any).component === 'radio-grid'
          ) {
            console.log(
              'Setting ref for radio-grid question:',
              question.id,
              question.label
            );
          }
          // Only register ref if context is available
          if (registerRef) {
            registerRef(question.id, el);
          }
        }
      }}
    >
      <QuestionLabel  
        $mediumText={question.isSubQuestion} //if the question is a subquestion show the main question in a medium font
        className={`${question.required ? 'required' : ''}`}
      >
        {/* <span>{question.q_number || ''}</span> */}
        <span className="wrap-text break-words">
          {question.label}{renderTooltip()}
        </span>
        <span className="ml-2 inline-flex items-end justify-center">
          {renderCertificateOverrideNotice()}
        </span>
      </QuestionLabel>
      {question.helpText && (
        <QuestionHelpText>{question.helpText}</QuestionHelpText>
      )}

      {/* Only render the field if there are no subquestions OR if it's a custom type */}
      {(question.type === 'custom' ||
        !question.subQuestions ||
        question.subQuestions.length === 0) &&
        renderField()}

      {question.note && <QuestionNote>{question.note}</QuestionNote>}

      {/* Render subquestions if they exist and if the there is no custom component */}
      {question.type !== 'custom' &&
        question.subQuestions &&
        question.subQuestions.length > 0 && (
          <div
            style={{
              marginTop: 'var(--spacing-4)',
            }}
          >
            {question.subQuestions
              .filter(subQuestion => !subQuestion.excluded) // Filter out excluded subQuestions
              .map((subQuestion, index) => (
                <div
                  key={subQuestion.id}
                  ref={el => {
                    // Only register ref if context is available
                    if (registerRef) {
                      registerRef(subQuestion.id, el);
                    }
                  }}
                  style={{
                    marginBottom:
                      index <
                      question.subQuestions!.filter(sq => !sq.excluded).length -
                        1
                        ? 'var(--spacing-4)'
                        : '0',
                  }}
                >
                  <QuestionField
                    question={subQuestion}
                    value={
                      answers?.[subQuestion.id]?.value ??
                      subQuestion.defaultValue
                    }
                    onChange={(value: any) =>
                      onAnswerChange?.(subQuestion.id, value)
                    }
                    answers={answers}
                    onAnswerChange={() => {}}
                    rejectionReasons={rejectionReasons}
                    onReviewChange={onReviewChange}
                    onUploadCompleteInSources={onUploadCompleteInSources}
                    {...(hasCountryChangedRef && { hasCountryChangedRef })}
                    {...(hasDestinationChangedRef && { hasDestinationChangedRef })}
                  />
                </div>
              ))}
          </div>
        )}
    </QuestionWrapper>
  );
};
